import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Trash2, UserPlus } from 'lucide-react';
import {
  addClientMember,
  removeClientMember,
  updateClientMember,
  type ClientMember,
} from '../../lib/clientsApi';
import { usePeople } from '../../hooks/usePeople';
import { usePermissions } from '../../hooks/usePermissions';
import { Avatar } from '../tasks/linear/Avatar';
import { Select } from '../ui/Select';
import { useConfirm } from '../ui/ConfirmProvider';

// Suggestions only: the role is free text, an agency names its own.
const ROLE_SUGGESTIONS = [
  'Account manager',
  'Content creator',
  'Editor video',
  'Designer',
  'Social media',
  'Ads',
  'Copywriter',
];

interface ClientTeamTabProps {
  clientId: string;
  members: ClientMember[];
}

/**
 * Echipa clientului: the colleagues who work on this client. Membership gives
 * each of them access to every project of the client (and its default
 * container), whatever their project scope says.
 */
export function ClientTeamTab({ clientId, members: initialMembers }: ClientTeamTabProps) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { can } = usePermissions();
  const canEdit = can('clients', 'canEdit');
  const { data: people } = usePeople({ active: 'true' });
  const [members, setMembers] = useState(initialMembers);
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const candidates = useMemo(() => {
    const taken = new Set(members.map((m) => m.userId));
    return (people ?? [])
      .filter((p) => p.userId && !taken.has(p.userId))
      .map((p) => ({ value: p.userId as string, label: p.fullName }));
  }, [people, members]);

  async function run(action: () => Promise<ClientMember[]>) {
    setBusy(true);
    setError(null);
    try {
      setMembers(await action());
      void qc.invalidateQueries({ queryKey: ['clients'] });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'A apărut o eroare.');
    } finally {
      setBusy(false);
    }
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!userId) return;
    await run(() => addClientMember(clientId, { userId, role: role.trim() || null }));
    setUserId('');
    setRole('');
  }

  async function handleRemove(m: ClientMember) {
    if (!(await confirm({ message: `Scoți pe ${m.user.name} din echipa clientului?` }))) return;
    await run(() => removeClientMember(clientId, m.id));
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Oamenii din echipa clientului văd toate proiectele, sarcinile și postările lui.
      </p>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-card">
        {members.length === 0 && (
          <p className="px-4 py-6 text-center text-sm text-gray-400">
            Nimeni nu e încă în echipa acestui client.
          </p>
        )}
        {members.map((m) => (
          <div key={m.id} className="flex items-center gap-3 px-4 py-3">
            <Avatar name={m.user.name} userId={m.user.id} src={m.user.avatarUrl} size={32} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-gray-900">{m.user.name}</p>
              <p className="truncate text-xs text-gray-500">{m.user.email}</p>
            </div>
            {canEdit ? (
              <input
                list="client-team-roles"
                defaultValue={m.role ?? ''}
                placeholder="Rol"
                onBlur={(e) => {
                  const next = e.target.value.trim() || null;
                  if (next !== m.role) void run(() => updateClientMember(clientId, m.id, next));
                }}
                className="w-40 rounded border border-gray-300 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none"
              />
            ) : (
              m.role && <span className="text-sm text-gray-600">{m.role}</span>
            )}
            {canEdit && (
              <button
                type="button"
                onClick={() => void handleRemove(m)}
                disabled={busy}
                className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                aria-label={`Scoate pe ${m.user.name}`}
              >
                <Trash2 size={15} />
              </button>
            )}
          </div>
        ))}
      </div>

      {canEdit && (
        <form
          onSubmit={(e) => void handleAdd(e)}
          className="flex flex-col gap-2 sm:flex-row sm:items-end"
        >
          <div className="sm:w-64">
            <Select
              label="Coleg"
              searchable
              value={userId}
              onChange={setUserId}
              placeholder="Alege un coleg"
              options={candidates}
            />
          </div>
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 sm:w-48">
            Rol
            <input
              list="client-team-roles"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="ex. Editor video"
              className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            />
          </label>
          <button
            type="submit"
            disabled={!userId || busy}
            className="inline-flex items-center justify-center gap-1.5 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            <UserPlus size={15} /> Adaugă în echipă
          </button>
        </form>
      )}

      <datalist id="client-team-roles">
        {ROLE_SUGGESTIONS.map((r) => (
          <option key={r} value={r} />
        ))}
      </datalist>
    </div>
  );
}
